import notificationService from './notificationService';
import reminderBackendApiService from './reminderBackendApiService';

class ReminderSchedulerService {
  constructor() {
    this.scheduledReminders = new Map();
    this.loadFromStorage();
    this.startDailyCheck();
  }

  // Load scheduled reminders from localStorage
  loadFromStorage() {
    try {
      const stored = localStorage.getItem('mostre-scheduled-reminders');
      if (stored) {
        const data = JSON.parse(stored);
        this.scheduledReminders = new Map(data);
      }
    } catch (error) {
      console.error('Error loading scheduled reminders from storage:', error);
    }
  }

  // Save scheduled reminders to localStorage
  saveToStorage() {
    try {
      localStorage.setItem('mostre-scheduled-reminders', JSON.stringify([...this.scheduledReminders]));
    } catch (error) {
      console.error('Error saving scheduled reminders to storage:', error);
    }
  }

  // Schedule a reminder for a person
  async scheduleReminder(campaignId, appointmentId, name, lastname, phone) {
    const key = `${campaignId}_${appointmentId}`;
    const now = new Date();
    
    try {
      // Schedule reminder on backend (this will send the confirmation notification)
      const backendResponse = await reminderBackendApiService.scheduleReminder(
        campaignId, 
        appointmentId, 
        name, 
        lastname, 
        phone
      );

      // No need to create local notification - backend handles it
      
      // Store locally for quick access (sync with backend data)
      const reminderData = {
        campaignId,
        appointmentId,
        name,
        lastname,
        phone,
        nextReminderDate: backendResponse.nextReminderDate,
        createdAt: now.toISOString(),
        lastReminderSent: now.toISOString(),
        backendReminderId: backendResponse.reminderId
      };
      
      this.scheduledReminders.set(key, reminderData);
      this.saveToStorage();
      
      console.log(`Reminder scheduled for ${name} ${lastname} - backend ID: ${backendResponse.reminderId}`);
      return backendResponse;
    } catch (error) {
      console.error('Error scheduling reminder:', error);
      // Fallback to local scheduling if backend fails
      const nextReminderDate = new Date(now.getTime() + (6 * 7 * 24 * 60 * 60 * 1000));
      
      const reminderData = {
        campaignId,
        appointmentId,
        name,
        lastname,
        phone,
        nextReminderDate: nextReminderDate.toISOString(),
        createdAt: now.toISOString(),
        lastReminderSent: now.toISOString(),
        backendError: true
      };
      
      this.scheduledReminders.set(key, reminderData);
      this.saveToStorage();
      notificationService.createFollowUpNotification(name, lastname, phone);
      
      throw error;
    }
  }

  // Cancel a reminder
  async cancelReminder(campaignId, appointmentId) {
    const key = `${campaignId}_${appointmentId}`;
    
    try {
      // Cancel on backend first
      await reminderBackendApiService.cancelReminder(campaignId, appointmentId);
      
      // Remove from local storage
      if (this.scheduledReminders.has(key)) {
        this.scheduledReminders.delete(key);
        this.saveToStorage();
        console.log(`Reminder cancelled for key: ${key}`);
      }
    } catch (error) {
      console.error('Error cancelling reminder on backend:', error);
      // Still remove locally even if backend fails
      if (this.scheduledReminders.has(key)) {
        this.scheduledReminders.delete(key);
        this.saveToStorage();
        console.log(`Reminder cancelled locally for key: ${key}`);
      }
      throw error;
    }
  }

  // Check for due reminders (called daily at 10 AM CET)
  async checkDueReminders() {
    try {
      // Check due reminders on backend (this will process and send notifications automatically)
      const backendResponse = await reminderBackendApiService.checkDueReminders();
      
      console.log(`Backend processed ${backendResponse.processedReminders?.length || 0} due reminders`);
      
      // Also check for due follow-up reminders
      const followUpResponse = await reminderBackendApiService.checkDueFollowUpReminders();
      
      console.log(`Backend processed ${followUpResponse.processedFollowUps?.length || 0} due follow-up reminders`);
      
      // Update local storage with any processed reminders
      if (backendResponse.processedReminders) {
        backendResponse.processedReminders.forEach(processed => {
          const key = `${processed.campaignId || 'unknown'}_${processed.appointmentId || processed.reminderId}`;
          if (this.scheduledReminders.has(key)) {
            const existing = this.scheduledReminders.get(key);
            this.scheduledReminders.set(key, {
              ...existing,
              nextReminderDate: processed.nextReminderDate,
              lastReminderSent: new Date().toISOString()
            });
          }
        });
        
        if (backendResponse.processedReminders.length > 0) {
          this.saveToStorage();
        }
      }
      
      const totalProcessed = (backendResponse.processedReminders?.length || 0) + (followUpResponse.processedFollowUps?.length || 0);
      return totalProcessed;
    } catch (error) {
      console.error('Error checking due reminders on backend:', error);
      
      // Fallback to local check if backend is unavailable
      return this.checkDueRemindersLocal();
    }
  }

  // Local fallback method for checking due reminders
  checkDueRemindersLocal() {
    const now = new Date();
    const dueReminders = [];

    for (const [key, reminder] of this.scheduledReminders) {
      const nextReminderDate = new Date(reminder.nextReminderDate);
      
      if (now >= nextReminderDate) {
        dueReminders.push({ key, reminder });
      }
    }

    // Process due reminders locally
    dueReminders.forEach(({ key, reminder }) => {
      // Send notification
      notificationService.createFollowUpNotification(
        reminder.name, 
        reminder.lastname, 
        reminder.phone
      );

      // Schedule next reminder (6 weeks from now)
      const nextReminderDate = new Date(now.getTime() + (6 * 7 * 24 * 60 * 60 * 1000));
      
      const updatedReminder = {
        ...reminder,
        nextReminderDate: nextReminderDate.toISOString(),
        lastReminderSent: now.toISOString()
      };
      
      this.scheduledReminders.set(key, updatedReminder);
      
      console.log(`Reminder sent locally for ${reminder.name} ${reminder.lastname} - next reminder: ${nextReminderDate.toLocaleDateString()}`);
    });

    if (dueReminders.length > 0) {
      this.saveToStorage();
    }

    return dueReminders.length;
  }

  // Start the daily check (simulated - in real app you'd use a proper scheduler)
  startDailyCheck() {
    // Check immediately
    this.checkDueReminders();

    // Then check every hour (in a real app, you'd want to check at specific times)
    setInterval(() => {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      
      // Check at 10 AM CET (adjust for timezone as needed)
      if (hours === 10 && minutes === 0) {
        this.checkDueReminders();
      }
    }, 60000); // Check every minute to catch the 10:00 AM moment
  }

  // Get all scheduled reminders
  getScheduledReminders() {
    return Array.from(this.scheduledReminders.values());
  }

  // Force check reminders (for testing)
  forceCheckReminders() {
    return this.checkDueReminders();
  }
}

// Create and export singleton instance
const reminderSchedulerService = new ReminderSchedulerService();
export default reminderSchedulerService;
